/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { ErrorToastService } from 'src/app/services/error-toast.service';
import { AuthService } from '../../services/auth-service.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private router: Router,
    private errorToastService: ErrorToastService
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let authRequest = request.clone();
    // hack
    if (
      request.url.indexOf('usermanagement/registration/refresh-token') === -1
    ) {
      authRequest = request.clone({
        headers: request.headers.append(
          'authorization',
          this.authService.getAccessToken()
        )
      });
    }

    return next.handle(authRequest).pipe(
      catchError(error => {
        if (error.status === 401) {
          if (request.url.split('/').includes('platform-login')) {
            // Сам вход не удался — пользователь не залогинен, обновлять нечего
            return throwError(() => error);
          }
          if (
            request.url.indexOf(
              'usermanagement/registration/refresh-token'
            ) !== -1
          ) {
            // 401 на САМОМ обновлении = refresh-токен мёртв (протух/отозван/разлогин на другом
            // устройстве). Здесь нельзя запускать ещё одно обновление: это рекурсия и дедлок.
            // Выходим из сессии; ошибка обновления через общий поток (shareReplay) дойдёт до всех
            // ожидающих 401, а logout идемпотентен.
            this.authService.logout();
            return throwError(() => error);
          }
          if (!this.authService.getRefreshToken()) {
            // Сессии нет (гость): 401 — это ШТАТНАЯ ошибка эндпоинта без авторизации (reset-password,
            // подтверждение регистрации, использованный код имперсонации). Обновлять нечего, разлогин
            // с reload недопустим — иначе гостя выбрасывало бы со страницы входа/регистрации/имперсонации.
            // Трактуем как обычную ошибку и отдаём компоненту.
            this.applyErrorSideEffects(error);
            return throwError(() => error);
          }
          return this.handle401Error(request, next);
        }

        // Не-401: побочные эффекты по статусу, затем проброс. Ровно та же обработка применяется к
        // повтору после обновления токена (см. handle401Error) — иначе одно и то же состояние
        // сервера давало бы разный исход в зависимости от того, истёк ли токен на этом запросе.
        this.applyErrorSideEffects(error);
        return throwError(() => error);
      })
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.authService.refreshToken().pipe(
      // Ошибка ОБНОВЛЕНИЯ токена (не бизнес-запроса): продлить сессию нечем. Обрыв связи/таймаут —
      // остаёмся в сессии и повторим в следующий онлайн; 500 — временная ошибка сервера, тоже без
      // выхода (с уведомлением); прочее (мёртвый/невалидный refresh) — выходим. Обрабатывается ДО
      // switchMap, поэтому изолировано от ошибок самого повторного запроса.
      catchError(refreshErr => {
        if (!this.isConnectionError(refreshErr)) {
          if (this.isInternalServerError(refreshErr)) {
            this.errorToastService.sendError(refreshErr.status);
          } else {
            this.authService.logout();
          }
        }
        return throwError(() => refreshErr);
      }),
      switchMap(() =>
        // Обновление удалось — повторяем исходный запрос с новым токеном.
        next
          .handle(
            request.clone({
              headers: request.headers.append(
                'authorization',
                this.authService.getAccessToken()
              )
            })
          )
          .pipe(
            catchError(retryErr => {
              // Повтор с новым токеном снова 401 = токен отвергнут → выходим (без повторного
              // обновления, иначе цикл). Прочие статусы — это ШТАТНЫЙ ответ сервера (404/409/422/…):
              // разбираем так же, как без истечения токена, и НЕ роняем сессию. Раньше любая не-500
              // ошибка повтора вела к logout, и обычная бизнес-ошибка на просроченном токене выкидывала
              // пользователя.
              if (retryErr.status === 401) {
                this.authService.logout();
              } else {
                this.applyErrorSideEffects(retryErr);
              }
              return throwError(() => retryErr);
            })
          )
      )
    );
  }

  /**
   * Побочные эффекты по статусу ошибки, единые для обычного пути и для повтора после обновления
   * токена. Держим в одном месте, чтобы исход не зависел от того, истёк ли токен на запросе.
   */
  private applyErrorSideEffects(error: { status: number }): void {
    switch (error.status) {
      case 200:
        this.errorToastService.sendError(error.status);
        break;
      default:
        break;
    }
  }

  private isConnectionError(err: { status: number, name?: string }): boolean {
    return (err.status === 0 || err.name === 'TimeoutError');
  }

  private isInternalServerError(err: { status: number }): boolean {
    return err.status === 500;
  }
}
